<template>
  <v-form
    v-model="formularioValido"
    ref="editarAdjudicacion">
    <v-card>
      <!-- Header -->
      <v-card-title class="py-3">
        <v-icon color="white" class="mr-2">mdi-file-document-edit</v-icon>
        Editar Adjudicación Directa
        <v-chip outlined small class="ml-2">
          <v-icon small left>mdi-identifier</v-icon>
          {{ adjudicacion.numeroDictamen }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn icon @click="cerrar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-6" style="max-height: 70vh; overflow-y: auto;">
        <!-- Paso 1: Identificación de la Adjudicación -->
        <v-row>
          <v-col cols="12">
            <div class="subtitle-2 primary--text mb-2">
              <v-icon small color="primary" class="mr-1">mdi-numeric-1-circle</v-icon>
              Identificación de la Adjudicación
            </div>
            <v-divider class="mb-3"></v-divider>
          </v-col>
        </v-row>
        <v-row>
          <custom-text-field
            cols="12"
            md="4"
            v-model="adjudicacion.numeroDictamen"
            label="Número de Dictamen"
            :rules="[mixReglas.requerido]">
            <template v-slot:prepend-inner>
              <v-icon small>mdi-file-certificate</v-icon>
            </template>
          </custom-text-field>
          <custom-text-field
            cols="12"
            md="4"
            v-model="adjudicacion.numeroContrato"
            label="Número de Contrato"
            :rules="[mixReglas.requerido]">
            <template v-slot:prepend-inner>
              <v-icon small>mdi-file-sign</v-icon>
            </template>
          </custom-text-field>
          <custom-date-picker
            cols="12"
            md="4"
            v-model="adjudicacion.fecha"
            label="Fecha de Adjudicación"
            :rules="[mixReglas.requerido]">
          </custom-date-picker>
        </v-row>

        <!-- Paso 2: Objeto de la Adjudicación -->
        <v-row class="mt-4">
          <v-col cols="12">
            <div class="subtitle-2 primary--text mb-2">
              <v-icon small color="primary" class="mr-1">mdi-numeric-2-circle</v-icon>
              Objeto de la Adjudicación
            </div>
            <v-divider class="mb-3"></v-divider>
          </v-col>
        </v-row>
        <v-row>
          <custom-text-area
            cols="12"
            v-model="adjudicacion.meta"
            label="Descripción / Meta"
            rows="3"
            :rules="[mixReglas.requerido]"
            hint="Describa el objeto o propósito de esta adjudicación"
            persistent-hint>
            <template v-slot:prepend-inner>
              <v-icon small>mdi-text-box</v-icon>
            </template>
          </custom-text-area>
        </v-row>

        <!-- Paso 3: Contratista -->
        <v-row class="mt-4">
          <v-col cols="12">
            <div class="subtitle-2 primary--text mb-2">
              <v-icon small color="primary" class="mr-1">mdi-numeric-3-circle</v-icon>
              Contratista
            </div>
            <v-divider class="mb-3"></v-divider>
          </v-col>
        </v-row>
        <v-row align="center">
          <custom-autocomplete
            cols="12"
            md="11"
            v-model="adjudicacion.idContratista"
            label="Seleccionar Contratista"
            :items="contratistas"
            item-text="nombreRazonSocial"
            item-value="id"
            clearable
            :rules="[mixReglas.requerido]">
            <template v-slot:prepend-inner>
              <v-icon small>mdi-domain</v-icon>
            </template>
          </custom-autocomplete>
          <v-col cols="12" md="1" class="d-flex justify-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <registrar-nuevo-contratista
                  onlyIcon
                  v-bind="attrs"
                  v-on="on" />
              </template>
              <span>Agregar nuevo contratista</span>
            </v-tooltip>
          </v-col>
        </v-row>

        <!-- Paso 4: Obras Asociadas -->
        <v-row class="mt-4">
          <v-col cols="12">
            <div class="subtitle-2 primary--text mb-2">
              <v-icon small color="primary" class="mr-1">mdi-numeric-4-circle</v-icon>
              Obras Asociadas
              <v-chip x-small color="primary" dark class="ml-2">
                {{ obrasAsociadas.length }}
              </v-chip>
            </div>
            <v-divider class="mb-3"></v-divider>
          </v-col>
        </v-row>

        <!-- Lista de Obras (Solo visualización) -->
        <v-row v-if="obrasAsociadas.length > 0">
          <v-col cols="12">
            <v-card outlined>
              <v-card-subtitle class="font-weight-bold pb-0">
                <v-icon small class="mr-1">mdi-format-list-checks</v-icon>
                Obras en la Adjudicación ({{ obrasAsociadas.length }})
              </v-card-subtitle>
              <v-divider class="mt-2"></v-divider>
              <v-list dense>
                <v-list-item
                  v-for="(obraItem, index) in obrasAsociadas"
                  :key="`obra-${obraItem.id}`"
                  :class="index % 2 === 0 ? 'grey lighten-5' : ''">
                  <v-list-item-avatar>
                    <v-chip small color="primary" dark>
                      {{ index + 1 }}
                    </v-chip>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      {{ obraItem.obra ? obraItem.obra.clave : 'Sin clave' }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ obraItem.obra ? obraItem.obra.meta : 'Sin descripción' }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action class="d-flex flex-row align-center">
                    <v-chip color="success" dark small class="mr-2">
                      <v-icon small left>mdi-currency-usd</v-icon>
                      {{ obraItem.importe | currency }}
                    </v-chip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="error"
                          v-bind="attrs"
                          v-on="on"
                          :loading="obraEliminando === obraItem.id"
                          :disabled="obraEliminando !== null"
                          @click="confirmarRemoverObra(obraItem)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Remover obra de la adjudicación</span>
                    </v-tooltip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col cols="12">
            <v-alert type="info" dense text border="left">
              <v-icon small left>mdi-information</v-icon>
              No hay obras asociadas a esta adjudicación
            </v-alert>
          </v-col>
        </v-row>

        <!-- Agregar Nuevas Obras -->
        <v-row class="mt-3">
          <v-col cols="12">
            <v-expansion-panels flat>
              <v-expansion-panel>
                <v-expansion-panel-header class="primary--text">
                  <div class="subtitle-2">
                    <v-icon small color="primary" class="mr-1">mdi-plus-circle</v-icon>
                    Agregar Nuevas Obras
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <obras-picker
                    ref="obrasPicker"
                    sinBordes
                    @obras-seleccionadas="handleObrasSeleccionadas">
                  </obras-picker>

                  <!-- Lista de Obras Seleccionadas para Agregar -->
                  <v-card v-if="obrasParaAgregar.length > 0" outlined class="mt-3">
                    <v-card-subtitle class="font-weight-bold pb-0">
                      <v-icon small class="mr-1">mdi-playlist-plus</v-icon>
                      Obras a Agregar ({{ obrasParaAgregar.length }})
                    </v-card-subtitle>
                    <v-divider class="mt-2"></v-divider>
                    <v-list dense>
                      <v-list-item
                        v-for="obra in obrasParaAgregar"
                        :key="`nueva-obra-${obra.id}`"
                        class="py-1">
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-bold">
                            {{ obra.clave }}
                          </v-list-item-title>
                          <v-list-item-subtitle class="text-caption">
                            {{ obra.meta }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action style="min-width: 200px;">
                          <custom-text-field
                            cols="12"
                            v-model.number="obra.importe"
                            label="Importe"
                            type="number"
                            prefix="$"
                            dense
                            hide-details
                            :rules="[mixReglas.requerido]">
                          </custom-text-field>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        :loading="agregandoObras"
                        :disabled="!puedeAgregarObras"
                        @click="agregarObrasSeleccionadas">
                        <v-icon left>mdi-plus</v-icon>
                        Agregar Obras ({{ obrasParaAgregar.length }})
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>

        <!-- Paso 5: Monto de la Adjudicación -->
        <v-row class="mt-4">
          <v-col cols="12">
            <div class="subtitle-2 primary--text mb-2">
              <v-icon small color="primary" class="mr-1">mdi-numeric-5-circle</v-icon>
              Monto de la Adjudicación
            </div>
            <v-divider class="mb-3"></v-divider>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4">
            <div class="subtitle-2 font-weight-bold mb-2">IMPORTE</div>
            <custom-text-field
              cols="12"
              v-model.number="adjudicacion.importe"
              label="Importe"
              type="number"
              prefix="$"
              :rules="[mixReglas.requerido]"
              @keyup="calcularTotal">
              <template v-slot:prepend-inner>
                <v-icon small>mdi-cash</v-icon>
              </template>
            </custom-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <div class="caption grey--text mb-2">IMPUESTO AL VALOR AGREGADO</div>
            <v-row dense>
              <custom-text-field
                cols="4"
                v-model.number="adjudicacion.porcentajeIva"
                label="% IVA"
                suffix="%"
                type="number"
                :rules="[mixReglas.requerido]"
                @keyup="calcularTotal">
              </custom-text-field>
              <custom-text-field
                cols="8"
                v-model="adjudicacion.iva"
                label="Importe IVA"
                prefix="$"
                readonly
                disabled>
              </custom-text-field>
            </v-row>
          </v-col>
          <v-col cols="12" md="4">
            <div class="success lighten-5 pa-3 rounded">
              <div class="subtitle-2 font-weight-bold success--text text--darken-2 mb-2">
                TOTAL
              </div>
              <custom-text-field
                cols="12"
                v-model="adjudicacion.total"
                label="Total"
                prefix="$"
                readonly
                disabled>
                <template v-slot:prepend-inner>
                  <v-icon small color="success">mdi-check-circle</v-icon>
                </template>
              </custom-text-field>
            </div>
          </v-col>
        </v-row>

        <!-- Paso 6: Vigencia y Condiciones -->
        <v-row class="mt-4">
          <v-col cols="12">
            <div class="subtitle-2 primary--text mb-2">
              <v-icon small color="primary" class="mr-1">mdi-numeric-6-circle</v-icon>
              Vigencia y Condiciones
            </div>
            <v-divider class="mb-3"></v-divider>
          </v-col>
        </v-row>
        <v-row>
          <custom-date-picker
            cols="12"
            md="3"
            v-model="adjudicacion.fechaInicio"
            label="Fecha de Inicio"
            :rules="[mixReglas.requerido]">
          </custom-date-picker>
          <custom-date-picker
            cols="12"
            md="3"
            v-model="adjudicacion.fechaTermino"
            label="Fecha de Término"
            :rules="[mixReglas.requerido]">
          </custom-date-picker>
          <custom-text-field
            cols="12"
            md="3"
            readonly
            v-model.number="adjudicacion.plazo"
            label="Plazo Calculado"
            suffix="días naturales">
            <template v-slot:prepend-inner>
              <v-icon small>mdi-clock-outline</v-icon>
            </template>
          </custom-text-field>
          <custom-text-field
            cols="12"
            md="3"
            v-model.number="adjudicacion.porcentajeAnticipo"
            label="Anticipo"
            suffix="%"
            type="number">
            <template v-slot:prepend-inner>
              <v-icon small>mdi-percent</v-icon>
            </template>
          </custom-text-field>
        </v-row>

        <!-- Información de Auditoría -->
        <v-row class="mt-4">
          <v-col cols="12">
            <v-expansion-panels flat>
              <v-expansion-panel>
                <v-expansion-panel-header class="grey lighten-4">
                  <div class="subtitle-2 grey--text text--darken-1">
                    <v-icon small class="mr-1">mdi-information-outline</v-icon>
                    Información del Registro
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense class="mt-2">
                    <v-col cols="12" md="6">
                      <div class="caption grey--text">Fecha de Creación</div>
                      <div class="body-2">
                        {{ adjudicacion.createdAt | formatoFechaHora }}
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="caption grey--text">Última Actualización</div>
                      <div class="body-2">
                        {{ adjudicacion.updatedAt | formatoFechaHora }}
                      </div>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-chip outlined small color="grey">
          <v-icon small left>mdi-calendar-edit</v-icon>
          Editando registro
        </v-chip>
        <v-spacer></v-spacer>
        <custom-button
          @click="cerrar"
          tipo="formCancelar"
          label="Cancelar"
          class="mr-3">
        </custom-button>
        <custom-button
          @click="guardarCambios"
          :disabled="!formularioValido || enviandoFormulario"
          :loading="enviandoFormulario"
          tipo="formAceptar"
          label="Guardar Cambios">
        </custom-button>
      </v-card-actions>
    </v-card>

    <!-- Diálogo de confirmación para remover obra -->
    <v-dialog
      v-model="mostrarDialogoRemoverObra"
      max-width="450"
      persistent>
      <v-card>
        <v-card-title class="error white--text">
          <v-icon color="white" class="mr-2">mdi-alert</v-icon>
          Confirmar Eliminación
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="body-1 mb-2">
            ¿Está seguro que desea remover esta obra de la adjudicación?
          </p>
          <v-alert
            v-if="obraARemover"
            type="warning"
            dense
            text
            border="left"
            class="mb-0">
            <div class="font-weight-bold">
              {{ obraARemover.obra ? obraARemover.obra.clave : 'Sin clave' }}
            </div>
            <div class="caption">
              Importe: {{ obraARemover.importe | currency }}
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <custom-button
            @click="cancelarRemoverObra"
            tipo="formCancelar"
            label="Cancelar"
            class="mr-3">
          </custom-button>
          <v-btn
            color="error"
            @click="removerObra"
            :loading="obraEliminando !== null">
            <v-icon left>mdi-delete</v-icon>
            Remover
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script>
import EDITAR_ADJUDICACION_DIRECTA from '@/graphql/mutations/administracion/adjudicaciones/editarAdjudicacionDirecta.gql';
import ADJUDICACIONES_DIRECTAS from '@/graphql/querys/administracion/adjudicaciones/adjudicacionesDirectas.gql';
import CONTRATISTAS from '@/graphql/querys/administracion/catalogos/contratistas/contratistas.gql';
import RegistrarNuevoContratista from '@/components/administracion/configuracion/catalogos/contratistas/Nuevo.vue';
import CustomTextField from '@/components/inputs/TextField.vue';
import CustomButton from '@/components/buttons/Button.vue';
import CustomAutocomplete from '@/components/inputs/Autocomplete.vue';
import CustomTextArea from '@/components/inputs/TextArea.vue';
import CustomDatePicker from '@/components/inputs/DatePicker.vue';
import ObrasPicker from '@/components/ObrasListasAdjudicar.vue';
import mixAdjudicaciones from './mixAdjudicaciones';

export default {
  name: 'EditarAdjudicacion',
  mixins: [mixAdjudicaciones],
  components: {
    RegistrarNuevoContratista,
    CustomTextField,
    CustomButton,
    CustomAutocomplete,
    CustomTextArea,
    CustomDatePicker,
    ObrasPicker,
  },
  props: {
    adjudicacionDirectaEditar: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      formularioValido: false,
      enviandoFormulario: false,
      contratistas: [],
      // Variables para remover obra
      mostrarDialogoRemoverObra: false,
      obraARemover: null,
      obraEliminando: null,
      // Variables para agregar obras
      obrasParaAgregar: [],
      agregandoObras: false,
      adjudicacion: {
        id: null,
        idContratista: null,
        numeroContrato: null,
        meta: null,
        numeroDictamen: '',
        fecha: null,
        importe: null,
        porcentajeIva: 16,
        iva: null,
        total: null,
        porcentajeAnticipo: 0,
        fechaInicio: null,
        fechaTermino: null,
        plazo: 0,
        createdAt: null,
        updatedAt: null,
      },
      obrasAsociadas: [],
    };
  },
  apollo: {
    contratistas: {
      query: CONTRATISTAS,
    },
  },
  mounted() {
    this.cargarDatosAdjudicacion();
  },
  computed: {
    fechaInicio() {
      return this.adjudicacion.fechaInicio;
    },
    fechaTermino() {
      return this.adjudicacion.fechaTermino;
    },
    puedeAgregarObras() {
      // Verificar que todas las obras tengan importe válido
      return this.obrasParaAgregar.length > 0
        && this.obrasParaAgregar.every((obra) => obra.importe && obra.importe > 0);
    },
  },
  watch: {
    adjudicacionDirectaEditar: {
      handler() {
        this.cargarDatosAdjudicacion();
      },
      deep: true,
    },
    fechaInicio() {
      this.setPlazo();
    },
    fechaTermino() {
      this.setPlazo();
    },
    'adjudicacion.importe': {
      handler() {
        this.calcularTotal();
      },
    },
  },
  methods: {
    cargarDatosAdjudicacion() {
      if (!this.adjudicacionDirectaEditar) return;
      // Clonar los datos para no modificar el original directamente
      const datos = this.adjudicacionDirectaEditar;

      this.adjudicacion = {
        id: datos.id,
        idContratista: datos.idContratista,
        numeroContrato: datos.numeroContrato,
        meta: datos.meta,
        numeroDictamen: datos.numeroDictamen,
        fecha: datos.fecha ? datos.fecha.substring(0, 10) : null,
        importe: datos.importe,
        porcentajeIva: datos.porcentajeIva,
        iva: datos.iva,
        total: datos.total,
        porcentajeAnticipo: datos.porcentajeAnticipo || 0,
        fechaInicio: datos.fechaInicio ? datos.fechaInicio.substring(0, 10) : null,
        fechaTermino: datos.fechaTermino ? datos.fechaTermino.substring(0, 10) : null,
        plazo: datos.plazo || 0,
        createdAt: datos.createdAt,
        updatedAt: datos.updatedAt,
      };

      // Cargar obras asociadas (solo para visualización)
      this.obrasAsociadas = datos.obras || [];
    },
    cerrar() {
      this.$refs.editarAdjudicacion.resetValidation();
      this.$emit('cerrar');
    },
    setPlazo() {
      const { fechaInicio, fechaTermino } = this.adjudicacion;
      if (!fechaInicio || !fechaTermino) return;
      const a = this.$moment(fechaInicio);
      const b = this.$moment(fechaTermino);
      this.adjudicacion.plazo = Number(b.diff(a, 'days')) + 1;
    },
    calcularTotal() {
      const { importe, porcentajeIva } = this.adjudicacion;
      if (!importe || importe === 0) return;

      const importeNum = Number(importe);
      const porcentajeIVA = Number(porcentajeIva || 16);
      const importeIVA = this.$numberRound(parseFloat(importeNum * (porcentajeIVA / 100)));
      const total = this.$numberRound(parseFloat(importeNum + importeIVA));

      this.adjudicacion.iva = importeIVA;
      this.adjudicacion.total = total;
    },
    sumarImportesObras() {
      // Sumar los importes de todas las obras asociadas
      this.adjudicacion.importe = this.$ObjSumar(this.obrasAsociadas, 'importe');
      // Recalcular IVA y total
      this.calcularTotal();
    },
    confirmarRemoverObra(obraItem) {
      this.obraARemover = obraItem;
      this.mostrarDialogoRemoverObra = true;
    },
    cancelarRemoverObra() {
      this.obraARemover = null;
      this.mostrarDialogoRemoverObra = false;
    },
    async removerObra() {
      if (!this.obraARemover) return;

      this.obraEliminando = this.obraARemover.id;
      this.mostrarDialogoRemoverObra = false;

      const exito = await this.mixRemoverAdjudicacionDirectaObra({
        obraId: this.obraARemover.id,
        adjudicacionId: this.adjudicacion.id,
      });

      if (exito) {
        // Actualizar lista local
        this.obrasAsociadas = this.obrasAsociadas.filter(
          (o) => o.id !== this.obraARemover.id,
        );
        // Recalcular el importe total de la adjudicación
        this.sumarImportesObras();
      }

      this.obraEliminando = null;
      this.obraARemover = null;
    },
    handleObrasSeleccionadas(obras) {
      // Filtrar obras que ya están asociadas
      const idsObrasAsociadas = this.obrasAsociadas.map((o) => o.idObra || o.obra?.id);
      this.obrasParaAgregar = obras
        .filter((obra) => !idsObrasAsociadas.includes(obra.id))
        .map((obra) => ({
          ...obra,
          importe: obra.importe || 0,
        }));
    },
    async agregarObrasSeleccionadas() {
      if (!this.puedeAgregarObras) return;

      this.agregandoObras = true;
      const { porcentajeIva } = this.adjudicacion;

      // Crear promesas para todas las obras
      const promesas = this.obrasParaAgregar.map((obra) => this.mixAgregarAdjudicacionDirectaObra({
        idAdjudicacionDirecta: this.adjudicacion.id,
        idObra: obra.id,
        importe: obra.importe,
        porcentajeIva: porcentajeIva || 16,
      }));

      // Ejecutar todas las promesas
      const resultados = await Promise.all(promesas);

      // Filtrar obras agregadas exitosamente
      const obrasAgregadas = resultados.filter((r) => r !== null);

      // Agregar a la lista local (sin duplicar, el cache de Apollo ya se actualizó)
      obrasAgregadas.forEach((obraAgregada) => {
        // Verificar que no exista ya en la lista local antes de agregar
        const yaExiste = this.obrasAsociadas.some((o) => o.id === obraAgregada.id);
        if (!yaExiste) {
          this.obrasAsociadas.push(obraAgregada);
        }
      });

      if (obrasAgregadas.length > 0) {
        // Recalcular el importe total de la adjudicación
        this.sumarImportesObras();
        // Refrescar el listado de obras disponibles en el picker
        if (this.$refs.obrasPicker) {
          this.$refs.obrasPicker.refrescarListado();
        }
        this.mixMensajeSuccess(`${obrasAgregadas.length} obra(s) agregada(s) correctamente`);
      }

      // Limpiar lista de obras para agregar
      this.obrasParaAgregar = [];
      this.agregandoObras = false;
    },
    async guardarCambios() {
      if (!this.$refs.editarAdjudicacion.validate()) return;

      this.enviandoFormulario = true;
      this.mixEsCargando();

      const datosEditar = {
        idContratista: this.adjudicacion.idContratista,
        numeroContrato: this.adjudicacion.numeroContrato,
        meta: this.adjudicacion.meta,
        numeroDictamen: this.adjudicacion.numeroDictamen,
        fecha: this.adjudicacion.fecha,
        importe: Number(this.adjudicacion.importe),
        porcentajeIva: Number(this.adjudicacion.porcentajeIva),
        iva: Number(this.adjudicacion.iva),
        total: Number(this.adjudicacion.total),
        porcentajeAnticipo: Number(this.adjudicacion.porcentajeAnticipo || 0),
        fechaInicio: this.adjudicacion.fechaInicio,
        fechaTermino: this.adjudicacion.fechaTermino,
        plazo: Number(this.adjudicacion.plazo),
      };

      try {
        await this.$apollo.mutate({
          mutation: EDITAR_ADJUDICACION_DIRECTA,
          variables: {
            id: this.adjudicacion.id,
            adjudicacionDirecta: datosEditar,
          },
          update: (store, { data: { actualizarAdjudicacionDirecta } }) => {
            try {
              const data = store.readQuery({ query: ADJUDICACIONES_DIRECTAS });
              const index = data.adjudicacionesDirectas.findIndex(
                (adj) => adj.id === actualizarAdjudicacionDirecta.id,
              );
              if (index !== -1) {
                data.adjudicacionesDirectas.splice(index, 1, actualizarAdjudicacionDirecta);
                store.writeQuery({ query: ADJUDICACIONES_DIRECTAS, data });
              }
            } catch {
              // Si no existe en cache, no hacer nada
            }
          },
        });

        this.mixMensajeSuccess('Adjudicación actualizada correctamente');
        this.$emit('actualizado');
        this.cerrar();
      } catch (err) {
        this.mixMensajeError(err);
      } finally {
        this.enviandoFormulario = false;
        this.mixEsCargando();
      }
    },
  },
  filters: {
    currency(value) {
      if (!value) return '$0.00';
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
      }).format(value);
    },
    formatoFechaHora(fecha) {
      if (!fecha) return '-';
      const date = new Date(fecha);
      return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
#contenedor-editar-adjudicacion {
  display: inline;
}
</style>
